import clsx from 'clsx';
import type { BuildDerived } from '@/types';
import { classifyFOC, focBarPosition } from '@/lib/calculations';
import { Card } from '@/components/ui/Card';
import { DiagramBanner } from '@/components/ui/DiagramBanner';

interface FOCBarProps {
  derived: BuildDerived;
}

const calloutClass: Record<string, string> = {
  rose: 'bg-rose-950/50 border-rose-800/50 text-rose-400',
  amber: 'bg-amber-950/50 border-amber-800/50 text-amber-400',
  emerald: 'bg-emerald-950/50 border-emerald-800/50 text-emerald-400',
  violet: 'bg-violet-950/50 border-violet-800/50 text-violet-400',
};

export function FOCBar({ derived }: FOCBarProps) {
  const zone = classifyFOC(derived.foc);
  const left = focBarPosition(derived.foc);

  // Status pill follows zone color — emerald / violet → nominal, amber → warn, rose → fault.
  const bannerStatus: 'nominal' | 'warning' | 'compute' =
    zone.color === 'emerald' || zone.color === 'violet'
      ? 'nominal'
      : zone.color === 'rose'
        ? 'warning'
        : 'compute';

  return (
    <Card padding="none" className="overflow-hidden">
      <DiagramBanner
        id="MSS-FOC · MASS BALANCE"
        doc="SE-MSS"
        status={bannerStatus}
        statusLabel={
          // v115 Sparrow Hybrid tier labels
          derived.foc >= 19
            ? 'EXTREME'
            : derived.foc >= 15
              ? 'HIGH'
              : derived.foc >= 11
                ? 'RECOMMENDED'
                : derived.foc >= 8
                  ? 'STANDARD'
                  : 'LOW'
        }
      />
      <div className="p-6">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-semibold">FOC — Front of Center</h2>
        <span className="font-mono text-2xl font-bold tabular-nums">
          {derived.foc.toFixed(1)}%
        </span>
      </div>

      <div className="relative">
        {/* Zone color strip */}
        <div className="h-8 rounded-md overflow-hidden flex border border-zinc-800">
          <div className="flex-1 bg-rose-900/50" title="Too low" />
          <div className="flex-1 bg-amber-900/50" title="Low" />
          <div className="flex-1 bg-emerald-700/60" title="Hunting range" />
          <div className="flex-1 bg-emerald-500/60" title="EFOC" />
          <div className="flex-1 bg-violet-700/60" title="Extreme" />
        </div>

        {/* Zone labels */}
        <div className="grid grid-cols-5 text-[10px] font-mono text-zinc-500 mt-1">
          <div className="text-center">&lt;5</div>
          <div className="text-center">5–7</div>
          <div className="text-center">7–12</div>
          <div className="text-center">12–19</div>
          <div className="text-center">19+</div>
        </div>

        {/* Marker */}
        <div
          className="absolute -top-1 w-1 h-10 bg-white rounded-full shadow-lg transition-all duration-300"
          style={{ left: `${left}%` }}
        >
          <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-white text-zinc-950 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded whitespace-nowrap">
            {derived.foc.toFixed(1)}%
          </div>
        </div>
      </div>

      <div
        className={clsx(
          'mt-4 p-3 rounded-md text-sm border',
          calloutClass[zone.color],
        )}
      >
        <span className="font-semibold">{zone.label}</span>{' '}
        <span className="text-zinc-300">{zone.detail}</span>
      </div>
      </div>
    </Card>
  );
}
